'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { useClerk, useSignIn as useClerkSignIn } from '@clerk/nextjs';
import { LoadingMorph } from '@extern/ui/loading-morph';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuthErrorToastFromSearchParams } from '@/app/(auth)/_hooks/use-auth-error-toast-from-search-params';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { SSOCallback } from '@/app/(auth)/_partials/sso-callback';
import { useUtmParams } from '@/hooks/use-utm-params';
import { useSignInFlowStore } from '../_hooks/use-sign-in-flow-store';
import { resolveSecondFactorStrategy } from '../_utils/resolve-second-factor-strategy';
import { SignInChooseSessionExperiment } from './sign-in-choose-session-experiment';
import { SignInChooseStrategyExperiment } from './sign-in-choose-strategy-experiment';
import { SignInForgotPasswordExperiment } from './sign-in-forgot-password-experiment';
import { SignInResetPasswordExperiment } from './sign-in-reset-password-experiment';
import { SignInStartExperiment } from './sign-in-start-experiment';
import { SignInVerificationsExperiment } from './sign-in-verifications-experiment';
async function restoreVerificationsFromClerk(signIn: NonNullable<ReturnType<typeof useClerkSignIn>['signIn']>, router: ReturnType<typeof useRouter>, searchParams: ReturnType<typeof useSearchParams>): Promise<void> {
  const store = useSignInFlowStore.getState();
  try {
    if (signIn.status === 'needs_second_factor' || signIn.status === 'needs_client_trust') {
      const secondFactorStrategy = await resolveSecondFactorStrategy(signIn);
      if (secondFactorStrategy) {
        store.onChange({
          step: 'verifications',
          secondFactorStrategy,
          firstFactorMethod: 'google',
          identifier: signIn.identifier ?? '',
          isRedirecting: false
        });
        return;
      }
    }
  } catch {
    // Fall through to the clean-up below.
  }

  // No in-progress second factor to restore (the attempt expired or was
  // abandoned): drop back to a clean start screen and strip the stale param.
  store.reset();
  const params = new URLSearchParams(searchParams.toString());
  params.delete('step');
  const query = params.toString();
  router.replace(query ? `/sign-in?${query}` : '/sign-in');
}
function useSyncSignInStepWithUrl(): void {
  const searchParams = useSearchParams();
  const router = useRouter();
  const clerk = useClerk();
  const {
    signIn
  } = useClerkSignIn();
  const hasInitRef = useRef(false);
  const hasRestoredRef = useRef(false);

  // Runs once, synchronously, before the first paint to avoid flashing the
  // wrong step.
  if (!hasInitRef.current) {
    hasInitRef.current = true;
    const requestedStep = searchParams.get('step');
    const currentStep = useSignInFlowStore.getState().step;
    if (requestedStep !== 'verifications') {
      // Fresh landing on the sign-in page: start from scratch.
      useSignInFlowStore.getState().reset();
    } else if (currentStep !== 'verifications') {
      // Reload / deep-link onto the MFA screen: the in-memory store was wiped
      // but the SSO sign-in attempt still lives in Clerk. Show the redirect
      // loader until we can rebuild the verifications state from Clerk.
      useSignInFlowStore.getState().onChange({
        isRedirecting: true
      });
    }
    // requestedStep === 'verifications' && store already at 'verifications':
    // the SSO callback populated the store before navigating — keep it as-is.
  }
  useEffect(() => {
    if (hasRestoredRef.current || !clerk.loaded) {
      return;
    }
    if (searchParams.get('step') !== 'verifications') {
      return;
    }
    if (useSignInFlowStore.getState().step === 'verifications') {
      return;
    }
    hasRestoredRef.current = true;
    restoreVerificationsFromClerk(signIn, router, searchParams);
  }, [clerk.loaded, signIn, router, searchParams]);
}
export function SignIn() {
  const $ = _c(32);
  useUtmParams();
  useSyncSignInStepWithUrl();
  useAuthErrorToastFromSearchParams();
  const searchParams = useSearchParams();
  const step = useSignInFlowStore(_temp);
  const isRedirecting = useSignInFlowStore(_temp2);
  const {
    trackAuthScreenViewed,
    trackAuthUnauthenticatedRedirected
  } = useAuthEvents();
  const hasTrackedUnauthRedirectRef = useRef(false);
  const previousStepRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== searchParams || $[1] !== trackAuthUnauthenticatedRedirected) {
    t0 = () => {
      if (hasTrackedUnauthRedirectRef.current) {
        return;
      }
      const redirectUrl = searchParams.get("redirect_url");
      if (!redirectUrl) {
        return;
      }
      hasTrackedUnauthRedirectRef.current = true;
      trackAuthUnauthenticatedRedirected(redirectUrl);
    };
    t1 = [searchParams, trackAuthUnauthenticatedRedirected];
    $[0] = searchParams;
    $[1] = trackAuthUnauthenticatedRedirected;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== isRedirecting || $[5] !== step || $[6] !== trackAuthScreenViewed) {
    t2 = () => {
      if (isRedirecting || previousStepRef.current === step) {
        return;
      }
      previousStepRef.current = step;
      if (step === "start") {
        return;
      }
      const stepMap = {
        verifications: "verifications",
        "choose-strategy": "choose_strategy",
        choose: "choose_session",
        "forgot-password": "forgot_password",
        "reset-password": "reset_password",
        callback: "sso_callback"
      } as const;
      trackAuthScreenViewed("sign_in", step, stepMap[step]);
    };
    t3 = [isRedirecting, step, trackAuthScreenViewed];
    $[4] = isRedirecting;
    $[5] = step;
    $[6] = trackAuthScreenViewed;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  if (isRedirecting) {
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <LoadingMorph />;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  let t4;
  if ($[10] !== step) {
    t4 = step === "start" ? <SignInStartExperiment /> : null;
    $[10] = step;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== step) {
    t5 = step === "verifications" ? <SignInVerificationsExperiment /> : null;
    $[12] = step;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== step) {
    t6 = step === "choose-strategy" ? <SignInChooseStrategyExperiment /> : null;
    $[14] = step;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== step) {
    t7 = step === "choose" ? <SignInChooseSessionExperiment /> : null;
    $[16] = step;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== step) {
    t8 = step === "forgot-password" ? <SignInForgotPasswordExperiment /> : null;
    $[18] = step;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== step) {
    t9 = step === "reset-password" ? <SignInResetPasswordExperiment /> : null;
    $[20] = step;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== step) {
    t10 = step === "callback" ? <SSOCallback /> : null;
    $[22] = step;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t11 = <div className="flex w-full flex-col items-center justify-center">{t4}{t5}{t6}{t7}{t8}{t9}{t10}</div>;
    $[24] = t10;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
}
function _temp2(s_0) {
  return s_0.isRedirecting;
}
function _temp(s) {
  return s.step;
}